import React, { FC, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { Outlet, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { Button, Fade, Icon, formatNumber } from '@column/column-ui-kit';

import { CopyRow } from '../Transfers/_components/CopyRow';
import { CustomPageContext, TabbedPage } from '~/app/Layout';
import { ROUTE } from '~/app/routes';
import { PageHeader } from '~/components';
import { SimulateTransferModal } from '~/components/Dialog';
import { LogoLoading } from '~/elements';
import { useBankAccount } from '~/hooks';
import { usePinnedBankAccounts } from '~/lib/api/bankAccounts';
import { useNavigate } from '~/lib/navigation';
import { BankAccount } from '~/repositories/BankAccountRepository';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { Card, Flex, Inner } from '~/styles';

import { ButtonPin } from './_components/ButtonPin';
import { Balance, FormLabelAmount, FormTextAmount } from './Edit';

interface Params {
  id: string;
}

export interface BankAccountSectionProps {
  bankAccount: BankAccount;
}

const TabNavigation = styled(TabbedPage)`
  margin: 0;
`;

const StyledFade = styled(Fade)`
  margin-top: 10%;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: auto;

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 24px;

    > div {
      border-top: none;
    }
  }
`;

export const PageBankAccountSection: FC<CustomPageContext> = ({ entries }) => {
  const navigate = useNavigate();
  const {
    response: bankAccount,
    createRequest: fetchBankAccount,
    isLoading,
  } = useBankAccount({
    onError: (error) => {
      navigate(ROUTE.BANK_ACCOUNTS);

      addDangerNotification({
        content: error.message,
      });
    },
  });

  const { isSandbox, currentUser } = useSessionStore();
  const { addDangerNotification } = useNotificationStore();
  const { id } = useParams<keyof Params>() as Params;
  const { isPinned, togglePin } = usePinnedBankAccounts({ skipFetch: true });
  const [isSandboxModalOpen, setIsSandboxModalOpen] = useState<boolean>(false);

  useLayoutEffect(() => {
    if (id) {
      fetchBankAccount({ id });
    }
  }, [id]);

  useEffect(
    () =>
      useSessionStore.subscribe(
        (state) => ({
          isSandbox: state.isSandbox,
          isLoading: state.isLoading,
          defaultPlatformId: state.currentUser?.defaultPlatformId,
        }),
        (newState, oldState) => {
          if (!currentUser) {
            return;
          }

          if (newState.isSandbox !== oldState.isSandbox || newState.defaultPlatformId !== oldState.defaultPlatformId) {
            fetchBankAccount({ id });
          }
        }
      ),
    []
  );

  const outletContext = useMemo(() => (bankAccount ? { bankAccount } : undefined), [bankAccount]);

  return (
    <>
      <SimulateTransferModal
        isOpen={isSandboxModalOpen}
        onClose={() => setIsSandboxModalOpen(false)}
        accountNumberId={bankAccount?.defaultAccountNumberId}
        onSuccess={() => {
          setTimeout(() => {
            fetchBankAccount({ id });
          }, 200);
        }}
      />

      <StyledFade show={isLoading && !bankAccount}>
        <LogoLoading />
      </StyledFade>
      <Fade show={!isLoading || !!bankAccount}>
        <PageHeader
          text={id ? bankAccount?.displayName || bankAccount?.description || 'Account' : 'Create Account'}
          tabs={<TabNavigation entries={entries} />}
        >
          {id && isSandbox && (
            <Button onClick={() => setIsSandboxModalOpen(true)} size="small" icon={<Icon.Sandbox />}>
              Simulate Transfer
            </Button>
          )}

          {id && (
            <ButtonPin
              variant="secondary"
              isPinned={isPinned(id)}
              onClick={() => togglePin(id, bankAccount?.displayName ?? bankAccount?.description ?? '')}
            >
              Pin
            </ButtonPin>
          )}
        </PageHeader>

        {id && (
          <>
            <Inner px="var(--page-margin)">
              <Balance>
                <Flex flexDirection="column" gap={4}>
                  <FormTextAmount isNegative={(bankAccount?.balances?.availableAmount ?? 0) < 0} isAvailable>
                    {formatNumber(bankAccount?.balances?.availableAmount)}
                  </FormTextAmount>
                  <FormLabelAmount>Available</FormLabelAmount>
                </Flex>
                <Flex flexDirection="column" gap={4}>
                  <FormTextAmount isNegative={(bankAccount?.balances?.pendingAmount ?? 0) < 0}>
                    {formatNumber(bankAccount?.balances?.pendingAmount)}
                  </FormTextAmount>
                  <FormLabelAmount>Pending</FormLabelAmount>
                </Flex>
                <Flex flexDirection="column" gap={4}>
                  <FormTextAmount isNegative={(bankAccount?.balances?.lockedAmount ?? 0) < 0}>
                    {formatNumber(bankAccount?.balances?.lockedAmount)}
                  </FormTextAmount>
                  <FormLabelAmount>Locked</FormLabelAmount>
                </Flex>
                <Flex flexDirection="column" gap={4}>
                  <FormTextAmount isNegative={(bankAccount?.balances?.holdingAmount ?? 0) < 0}>
                    {formatNumber(bankAccount?.balances?.holdingAmount)}
                  </FormTextAmount>
                  <FormLabelAmount>Holding</FormLabelAmount>
                </Flex>
              </Balance>
            </Inner>

            <Inner pt={0} pb={16} px="var(--page-margin)">
              <Card variant="secondary">
                <Grid>
                  <CopyRow value={bankAccount?.routingNumber} label="Routing Number" />
                  <CopyRow value={bankAccount?.defaultAccountNumber} label="Account Number" />
                  <CopyRow value={bankAccount?.id} label="Account ID" isMonospaced />
                </Grid>
              </Card>
            </Inner>
          </>
        )}
        {outletContext && <Outlet context={outletContext} />}
      </Fade>
    </>
  );
};
